import {
  Button as MuiButton,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid as MuiGrid,
  TextField as MuiTextField,
  Typography as MuiTypography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Autocomplete } from '@material-ui/lab'
import { spacing } from '@material-ui/system'
import { AxiosError } from 'axios'
import { DropzoneAreaBase } from 'material-ui-dropzone'
import React, { ReactNode, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { ParcelOrder, ParcelShipment, ParcelShipmentStatusEnum } from 'src/api/models'
import { AttachmentProp } from 'src/components/Dialog/AddAttachmentsDialog'
import { FilePreview, UrlPreview } from 'src/components/FilePreview'
import { useQueryPaginator } from 'src/components/Table'
import { handleErrorMessage, Notification } from 'src/components/Notification'
import {
  AddressField,
  MapSelectField,
  TextareaField,
  TextField,
  QuerySelectField,
  useForm,
} from 'src/forms'
import { setApiErrors } from 'src/forms/errors'
import { skuit } from 'src/services/skuit'
import styled from 'styled-components'
import * as yup from 'yup'

const Button = styled(MuiButton)(spacing)
const Grid = styled(MuiGrid)(spacing)
const Typography = styled(MuiTypography)(spacing)

const useStyles = makeStyles(() => ({
  form: {
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
}))

interface AddressProps {
  company?: string
  consignee: string
  kphone?: string
  address1: string
  address2?: string
  address3?: string
  city: string
  state: string
  postal_code: string // eslint-disable-line camelcase
}

interface ShipmentProps {
  client?: string
  facility: string
  carrier: string
  tracking_number: string // eslint-disable-line camelcase
  internal_notes?: string // eslint-disable-line camelcase
  status: string
  address: AddressProps
  attachments?: AttachmentProp[]
}

const shipmentSchema = yup.object().shape({
  client: yup.string().required().label('Client'),
  order: yup.string().optional().label('Order'),
  facility: yup.string().required().label('Facility'),
  carrier: yup.string().label('Carrier'),
  tracking_number: yup.string().label('Tracking Number'),
  internal_notes: yup.string().nullable().label('Internal Notes'),
  status: yup.string().label('Status').required(),
  address: yup
    .object({
      company: yup.string().label('Company').optional(),
      consignee: yup.string().label('Consignee').required(),
      phone: yup.string().label('Phone Number').optional().nullable(),
      address1: yup.string().label('Address 1').required(),
      address2: yup.string().label('Address 2'),
      address3: yup.string().label('Address 3'),
      city: yup.string().label('City').required(),
      state: yup.string().label('State').required(),
      postal_code: yup.string().label('Postal Code').required(),
    })
    .required(),
  attachments: yup.array().of(
    yup
      .object({
        xid: yup.string().when('file', {
          is: (file) => !file,
          then: yup.string().required(),
        }),
        file: yup.mixed(),
        tags: yup.array().of(yup.string()),
      })
      .label('Attachments')
  ),
})

interface ImageListWrapperProps {
  children: ReactNode
}

const ImageListWrapper = (props: ImageListWrapperProps) => {
  const { children } = props
  return (
    <Grid container spacing={6} mt={6}>
      {children}
    </Grid>
  )
}

interface Props {
  open: boolean
  handleClose: () => void
  onSave?: () => void
  update?: boolean
  shipment?: ParcelShipment
  order?: ParcelOrder
}

export const ParcelShipmentDialog = (props: Props) => {
  const classes = useStyles()
  const { open, handleClose, onSave, update = false, order, shipment } = props

  const [saving, setSaving] = useState(false)
  const [clientGuid, setClientGuid] = useState<string | null>(shipment?.client?.guid || null)
  const [selectedOrder, setSelectedOrder] = useState<ParcelOrder | null>(shipment?.order || order || null)

  const clientPaginator = useQueryPaginator('client-list', skuit.sc.clients.list, {
    params: { search: shipment?.client?.name || order?.client?.name },
  })
  const orderPaginator = useQueryPaginator('order-list', skuit.sc.parcel.orders.list, {
    params: { search: shipment?.order?.number || order?.number },
  })
  const facilityPaginator = useQueryPaginator('facility-list', skuit.sc.facilities.list)
  const carrierPaginator = useQueryPaginator('carreir-list', skuit.sc.carriers.list)

  const tagPaginator = useQueryPaginator('tag-list', skuit.sc.attachments.tags.list)

  const { Form, Field, FieldList, data: formData } = useForm(shipmentSchema)

  const setFormData = formData.setValue
  useEffect(() => {
    if (order) {
      setSelectedOrder(order)
      setClientGuid(order.client?.guid || null)
    }
  }, [order, setSelectedOrder, setClientGuid, setFormData])

  const patchOrderFilters = orderPaginator.patchFilters
  useEffect(() => {
    if (clientGuid) {
      patchOrderFilters({ clients: clientGuid })
      if (!shipment && !order) {
        setFormData('order', undefined)
      }
    }
  }, [order, shipment, clientGuid, patchOrderFilters, setFormData])

  const onSubmit = (data: ShipmentProps) => {
    setSaving(true)
    const attachments = data.attachments
    delete data.attachments
    let func = skuit.sc.parcel.shipments.create
    let action = 'creat'
    if (update) {
      func = (data: ShipmentProps) => skuit.sc.parcel.shipments.patch(shipment?.guid || '', data)
      action = 'updat'
      delete data.client
    }

    func(data)
      .then((newShipment: ParcelShipment) => {
        if (attachments?.length) {
          skuit.sc.parcel.shipments.attachments
            .create(newShipment.guid as string, { attachments })
            .then(() => {
              handleClose()
              if (onSave) {
                onSave()
              }

              toast.success(
                <Notification
                  message={`Successfully ${action}ed Shipment: ${newShipment.name}`}
                  newObject={newShipment}
                />
              )
              toast.success(
                <Notification message={`Successfully added ${attachments.length} attachments!`} />
              )
            })
            .catch((attachmentError: AxiosError) => {
              handleErrorMessage(attachmentError, action, 'Attachments')
              setApiErrors(formData.setError, attachmentError)
            })
        } else {
          handleClose()
          if (onSave) {
            onSave()
          }
          toast.success(
            <Notification
              message={`Successfully ${action}ed Shipment: ${newShipment.name}`}
              newObject={newShipment}
            />
          )
        }
      })
      .catch((error: AxiosError) => {
        handleErrorMessage(error, action, 'Shipment')
        setApiErrors(formData.setError, error)
      })
      .finally(() => {
        setSaving(false)
      })
  }

  const isOnlyStatusChanges = shipment?.status === 'delivered' || shipment?.status === 'canceled'

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <Form formProps={{ className: classes.form }} onSubmit={onSubmit}>
        <DialogTitle>{shipment ? 'Update' : 'Create'} Shipment</DialogTitle>
        <DialogContent>
          <QuerySelectField
            name="client"
            label="Client"
            paginator={clientPaginator}
            defaultValue={shipment?.client || order?.client}
            disabled={update || !!order || isOnlyStatusChanges}
            onSelected={(client) => setClientGuid(client.guid)}
          />
          <QuerySelectField
            name="order"
            label="Order"
            paginator={orderPaginator}
            defaultValue={shipment?.order || selectedOrder}
            disabled={update || !clientGuid || !!order || isOnlyStatusChanges}
            onSelected={(order) => setSelectedOrder(order)}
          />
          <QuerySelectField
            name="facility"
            label="Facility"
            paginator={facilityPaginator}
            defaultValue={shipment?.facility || order?.facility}
            disabled={!clientGuid || isOnlyStatusChanges}
          />
          <QuerySelectField
            name="carrier"
            label="Carrier"
            paginator={carrierPaginator}
            defaultValue={shipment?.carrier || order?.carrier}
            disabled={!clientGuid || isOnlyStatusChanges}
            getOptionLabel={(option) => `${option.name}${option.scac ? ' [' + option.scac + ']' : ''}`}
            renderOption={(option) => (
              <span>
                {option.name}
                {option.scac && ` [${option.scac}]`}
              </span>
            )}
          />
          <TextField
            name="tracking_number"
            label="Tracking Number"
            defaultValue={shipment ? shipment.trackingNumber : undefined}
            disabled={!clientGuid || isOnlyStatusChanges}
          />
          <TextareaField
            name="internal_notes"
            label="Internal Notes"
            defaultValue={shipment ? shipment.internalNotes : undefined}
            disabled={!clientGuid || isOnlyStatusChanges}
          />
          <MapSelectField
            name="status"
            label="Status"
            options={ParcelShipment.Status}
            defaultValue={
              shipment?.status || ParcelShipmentStatusEnum.pending
            }
          />

          <Typography variant="h6" mt={12}>
            Shipping
          </Typography>
          <AddressField
            name="address"
            defaultValue={shipment?.address || selectedOrder?.address}
            disabled={!clientGuid || isOnlyStatusChanges}
          />

          <Typography variant="h6" mt={6} mb={3}>
            Attachments
          </Typography>
          <FieldList
            name="attachments"
            startCount={0}
            renderHeader={({ name, value, setValue }) =>
              shipment?.status === 'canceled' ? null : (
                <DropzoneAreaBase
                  dropzoneText="Drag and drop or click to add files"
                  fileObjects={[]}
                  onAdd={(files) => {
                    const fileData = files.map((fileObj) => {
                      return { file: fileObj.file, tags: [] }
                    })
                    const newValue = (value || []).concat(fileData)
                    setValue(name, newValue)
                  }}
                  filesLimit={10}
                  showPreviewsInDropzone={false}
                />
              )
            }
            renderList={ImageListWrapper}
            renderField={({ name: itemName }) => (
              <Grid item xs={3} key={itemName}>
                <Card>
                  <CardContent>
                    <Field
                      name="file"
                      renderField={({ value, remove, error }) => {
                        if (!value) {
                          return null
                        }
                        return (
                          <>
                            <FilePreview file={value} onDelete={() => remove(itemName)} />
                            {error && <FormHelperText error={!!error}>{error}</FormHelperText>}
                          </>
                        )
                      }}
                    />
                    <Field
                      name="url"
                      renderField={({ value, remove, error }) => {
                        if (!value) {
                          return null
                        }
                        return (
                          <>
                            <UrlPreview
                              url={value}
                              allowDownload={false}
                              onDelete={() => remove(itemName)}
                            />
                            {error && <FormHelperText error={!!error}>{error}</FormHelperText>}
                          </>
                        )
                      }}
                    />
                    <Field
                      name="tags"
                      getSubmitValue={(value) =>
                        value && value.map((tag: { slug: string }) => tag.slug)
                      }
                      renderField={({ name, value, setValue, error }) => (
                        <Autocomplete
                          id={name}
                          disabled={isOnlyStatusChanges}
                          multiple
                          freeSolo
                          options={tagPaginator.data.results}
                          getOptionLabel={(tag) => tag.name}
                          getOptionSelected={(option, value) => option.guid === value?.guid}
                          onChange={(event: any, newValue: any | null) => setValue(name, newValue)}
                          value={value}
                          renderInput={(params) => (
                            <MuiTextField
                              {...params}
                              error={!!error}
                              helperText={error}
                              label="Tags"
                            />
                          )}
                        />
                      )}
                    />
                  </CardContent>
                </Card>
              </Grid>
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button type="submit" color="primary" variant="contained" disabled={saving} autoFocus>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  )
}
